import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Pressable,
} from 'react-native';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb, KPIDeleteConfirmModal, ConfirmModal } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { initiativesApi, goalsApi, kpisApi, organizationalUnitsApi, ssoUsersApi, SSOUserBrief, partnersApi, Partner } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { StrategicGoalList, KPIType, Frequency, Polarity, OrganizationalUnitList } from '../../types';

type RouteProps = RouteProp<MainStackParamList, 'AddInitiative'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: string;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  startDate: string;
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  dataSource: string;
  isExpanded: boolean;
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DragHandleIcon = ({ size = 20, color = '#9CA3AF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 6H8.01M8 12H8.01M8 18H8.01M16 6H16.01M16 12H16.01M16 18H16.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 6H20M4 12H20M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : '#F1F5F9',
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : '#F1F5F9',
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      ref={menuCallbackRef}
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        left: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 12,
        maxHeight: 250,
        zIndex: 99999,
        elevation: 99999,
        overflow: 'hidden' as any,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
        <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <Text style={[styles.dropdownItemText, { color: dropdownTheme.text }]}>{option.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
          disabled && { backgroundColor: dropdownTheme.disabledBg }
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// MultiSelectDropdown Component
// ============================================================

interface MultiSelectDropdownProps {
  values: string[];
  options: DropdownOption[];
  placeholder: string;
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

const MultiSelectDropdown = ({ values, options, placeholder, onChange, disabled }: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();

  const selectedOptions = options.filter(opt => values.includes(opt.value));

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : '#F1F5F9',
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : '#F1F5F9',
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const toggleOption = (optionValue: string) => {
    if (values.includes(optionValue)) {
      onChange(values.filter(v => v !== optionValue));
    } else {
      onChange([...values, optionValue]);
    }
  };

  const removeOption = (optionValue: string) => {
    onChange(values.filter(v => v !== optionValue));
  };

  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      ref={menuCallbackRef}
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        left: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 12,
        maxHeight: 250,
        zIndex: 99999,
        elevation: 99999,
        overflow: 'hidden' as any,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
        <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
          {options.map((option) => {
            const isSelected = values.includes(option.value);
            return (
              <TouchableOpacity
                key={option.value}
                style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
                onPress={() => toggleOption(option.value)}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <View style={{
                    width: 20, height: 20, borderRadius: 4,
                    borderWidth: 2, borderColor: isSelected ? '#A9211F' : '#D1D5DB',
                    backgroundColor: isSelected ? '#A9211F' : 'transparent',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSelected && (
                      <Svg width={12} height={12} viewBox="0 0 24 24">
                        <Path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </Svg>
                    )}
                  </View>
                  <Text style={[styles.dropdownItemText, { color: dropdownTheme.text, flex: 1 }]}>{option.label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border, minHeight: 44 },
          disabled && { backgroundColor: dropdownTheme.disabledBg }
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        {selectedOptions.length === 0 ? (
          <Text style={[styles.dropdownButtonText, { color: dropdownTheme.placeholder }]}>
            {placeholder}
          </Text>
        ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, flex: 1, justifyContent: 'flex-end' }}>
            {selectedOptions.map(opt => (
              <View key={opt.value} style={{
                flexDirection: 'row', alignItems: 'center', gap: 4,
                backgroundColor: isDark ? '#374151' : '#F1F5F9',
                borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4,
              }}>
                <TouchableOpacity onPress={() => removeOption(opt.value)} hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
                  <Text style={{ color: '#EF4444', fontSize: 14, fontFamily: FONTS.regular }}>×</Text>
                </TouchableOpacity>
                <Text style={{ color: dropdownTheme.text, fontSize: 13, fontFamily: FONTS.regular }}>{opt.label}</Text>
              </View>
            ))}
          </View>
        )}
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// KPI Options
// ============================================================

// Note: These will be translated at render time using the t function
const getKpiTypeOptions = (t: (key: string) => string): DropdownOption[] => [
  // 'strategic' is intentionally NOT offered here. A strategic-type KPI
  // requires data_source_type (مستقل/تجميعي) per DTS 4.3 — a strategic-goal
  // cascade concept collected only on AddGoalScreen. An عنصر تشغيلي measures
  // operational performance, so offering 'strategic' here let users create a
  // KPI the API rejects with a 400 (data_source_type required), which silently
  // dropped the KPI on save.
  { value: 'operational', label: t('kpis.operational') },
  { value: 'efficiency', label: t('kpis.efficiency') },
  { value: 'effectiveness', label: t('kpis.effectiveness') },
  { value: 'impact', label: t('kpis.impact') },
];

const getPolarityOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'positive', label: t('kpis.ascending') },
  { value: 'negative', label: t('kpis.descending') },
];

const getUnitOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: '%', label: t('kpis.percentage') },
  { value: 'number', label: t('kpis.number') },
  { value: 'currency', label: t('kpis.currency') },
];

const getFrequencyOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'daily', label: t('kpis.daily') },
  { value: 'weekly', label: t('kpis.weekly') },
  { value: 'monthly', label: t('kpis.monthly') },
  { value: 'quarterly', label: t('kpis.quarterly') },
  { value: 'half_yearly', label: t('kpis.semiAnnual') },
  { value: 'yearly', label: t('kpis.annual') },
];


// ============================================================
// Main Component
// ============================================================

export default function AddInitiativeScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { goalId, initiativeId, scrollToKpi } = route.params || {};
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg, isArabic } = useAutoTranslate();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();

  // Proposing/editing an operational element (عنصر تشغيلي) is governed by
  // `hrcpms.executive_items.create` — NOT the coarse `isViewOnly` flag. The
  // Initiatives list only shows the "إضافة عنصر تشغيلي" button to these users
  // (superuser | executive_items.create), so this screen must accept them too.
  // Without this, a user who holds the create perm but is also flagged
  // view-only (Director / Strategy-Team, or a superuser tripping isStrategyTeam)
  // gets silently bounced the instant they tap Add — i.e. "the button does
  // nothing".
  const canCreateOperationalItem = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );

  useEffect(() => {
    if (isViewOnly && !canCreateOperationalItem) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, canCreateOperationalItem, navigation, navigateToHome]);

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F8FAFC',
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: colors.textPrimary,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : '#F1F5F9',
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
  };

  // Refs for scrolling to KPI section
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionY = useRef<number>(0);

  // Edit mode flag
  const isEditMode = !!initiativeId;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    initiativeType: 'initiative' as 'initiative' | 'project' | 'program' | 'operational_activity' | 'process',
    strategicGoal: goalId ? goalId.toString() : '',
    weight: '',
    responsiblePerson: '',
    organizationalUnits: [] as string[],
    resources: '',
    startDate: '',
    endDate: '',
    status: 'planning' as 'planning' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled',
    // Type-specific (DTS UC-03)
    partners: '',                       // legacy free-text — kept for read-only display of old data
    partnerIds: [] as string[],         // new: M2M to curated Partner catalog
    requiredAction: '',
    requiredAttachmentsCount: '0',
    employeesCount: '',
    executionFrequency: '',
    flowchartData: '',
    assignedEmployeeIds: [] as string[],
    // DTS UC-03 step 4: per-KPI contribution weights — list of
    // {kpiId, weight} pairs the Section Head sets for the strategic KPIs
    // this initiative serves. Sent to backend as `kpi_contributions`.
    kpiContributions: [] as Array<{ kpiId: string; weight: string }>,
  });

  // 5 operational element types per DTS spec
  const INITIATIVE_TYPES: { value: typeof formData.initiativeType; label: string }[] = [
    { value: 'initiative', label: 'مبادرة' },
    { value: 'project', label: 'مشروع' },
    { value: 'program', label: 'برنامج' },
    { value: 'operational_activity', label: 'نشاط تشغيلي' },
    // 'عملية' (process) removed — processes are a separate module, not an
    // initiative/operational-element type.
  ];

  // Frequency options for operational_activity / process
  const FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'يومي' },
    { value: 'weekly', label: 'أسبوعي' },
    { value: 'monthly', label: 'شهري' },
    { value: 'quarterly', label: 'ربعي' },
    { value: 'semi_annual', label: 'نصف سنوي' },
    { value: 'annual', label: 'سنوي' },
    { value: 'continuous', label: 'مستمر' },
  ];

  // Helper: which type-specific fields to show
  const showFlowchart = formData.initiativeType === 'process';
  const showFrequency = formData.initiativeType === 'operational_activity' || formData.initiativeType === 'process';
  const showEmployeesCount = formData.initiativeType === 'process' || formData.initiativeType === 'operational_activity';
  const isEndDateRequired = formData.initiativeType === 'project';

  // KPIs state
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);
  // When some KPIs fail to save we show a blocking dialog instead of a fleeting
  // toast (which the success message used to bury). `savedId` lets the user
  // jump straight to the saved element.
  const [kpiSaveError, setKpiSaveError] = useState<{ message: string; savedId: number } | null>(null);
  // Id of the element created this session. After a create we treat further
  // saves as updates of THIS element, so retrying failed KPIs can't spawn a
  // duplicate operational element.
  const [createdInitiativeId, setCreatedInitiativeId] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [organizationalUnits, setOrganizationalUnits] = useState<OrganizationalUnitList[]>([]);
  const [sectionEmployees, setSectionEmployees] = useState<SSOUserBrief[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  // Goal IDs whose strategic KPIs are cascaded to the current user's section.
  // null = "still loading or N/A (Strategy Team / DG see everything)".
  // empty Set = "no goals cascaded to this user — they cannot propose".
  const [cascadedGoalIds, setCascadedGoalIds] = useState<Set<number> | null>(null);
  // Full strategic KPI list the current user can pick from to populate
  // `kpi_contributions` (DTS UC-03 step 3-4). For Section Heads this is
  // limited to KPIs cascaded onto their section; for Strategy Team / DG
  // it's the full set of strategic KPIs in the system.
  const [cascadedKpis, setCascadedKpis] = useState<Array<{
    id: number;
    name: string;
    strategic_goal: number | null;
    strategic_goal_title?: string;
  }>>([]);

  useUnsavedChangesWarning(!!formData.title || !!formData.description);

  // Fetch goals for dropdown
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const response = await goalsApi.list();
        setGoals(response.results || []);
      } catch (error) {
        console.error('Error fetching goals:', error);
      } finally {
        setGoalsLoading(false);
      }
    };
    fetchGoals();
  }, []);

  // DTS UC-02 → UC-03 step 3: when the proposer is a Section Head (no
  // global strategic perms), only show goals that have at least one
  // strategic KPI cascaded onto their section. Strategy Team / DG keep
  // unrestricted access. We also keep the full KPI list around so the
  // contribution-weights picker (UC-03 step 4) has names/goal titles
  // without re-fetching.
  useEffect(() => {
    const isStrategyTeam = (user?.permissions || []).includes('hrcpms.strategic_goals.create');
    const isDgOrDirector = (user?.permissions || []).includes('hrcpms.kpi_cascade.view');
    const unrestricted = !!user && (user.is_superuser || isStrategyTeam || isDgOrDirector);

    if (unrestricted) {
      setCascadedGoalIds(null);
      (async () => {
        try {
          const resp = await kpisApi.list({ kpi_type: 'strategic', page_size: 500 });
          setCascadedKpis((resp.results || []) as any);
        } catch (err) {
          console.error('Failed to fetch strategic KPIs:', err);
        }
      })();
      return;
    }

    const userDeptId = user?.organization?.id;
    if (!userDeptId || !organizationalUnits.length) return;

    const anchor = organizationalUnits.find(u => (u as any).department_id === userDeptId);
    if (!anchor) {
      setCascadedGoalIds(new Set()); // no anchor → no goals visible
      setCascadedKpis([]);
      return;
    }
    (async () => {
      try {
        const resp = await kpisApi.list({ cascaded_to_section: anchor.id, page_size: 500 });
        const kpis = (resp.results || []) as any[];
        setCascadedKpis(kpis);
        const ids = new Set<number>(
          kpis
            .map((k: any) => k.strategic_goal)
            .filter((id: number | null) => typeof id === 'number') as number[]
        );
        setCascadedGoalIds(ids);
      } catch (err) {
        console.error('Failed to fetch cascaded KPIs:', err);
        setCascadedGoalIds(new Set()); // fail-closed for section heads
        setCascadedKpis([]);
      }
    })();
  }, [user, organizationalUnits]);

  // Fetch organizational units for dropdown
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await organizationalUnitsApi.list({ page_size: 9999 });
        setOrganizationalUnits(response.results || []);
      } catch (error) {
        console.error('Error fetching organizational units:', error);
      } finally {
        setUnitsLoading(false);
      }
    };
    fetchUnits();
  }, []);

  // Fetch curated partners catalog (active only) for the multi-select picker.
  // Inactive partners stay visible on existing initiatives via partner_links_details
  // but don't show up in the picker for new selections.
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await partnersApi.list({ is_active: true, page_size: 500 });
        setPartners(response.results || []);
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        setPartnersLoading(false);
      }
    };
    fetchPartners();
  }, []);

  // Fetch SSO users of current user's section (for execution-responsibility dropdown).
  // Uses /api/v1/sso-users/?organization=<dept_id> — same pattern as the
  // correspondence project's internal-recipient picker. SSO is the source of
  // truth for "people who can be assigned a task".
  useEffect(() => {
    const deptId = user?.organization?.id;
    if (!deptId) {
      setEmployeesLoading(false);
      return;
    }
    const fetchSectionUsers = async () => {
      try {
        const response = await ssoUsersApi.list({
          organization: deptId,
          is_active: true,
          page_size: 9999,
        });
        const list = response.results || [];
        // Exclude the current user — section head shouldn't reassign to himself
        const filtered = list.filter((u) => u.id !== user?.id);
        setSectionEmployees(filtered);
      } catch (error) {
        console.error('Error fetching section SSO users:', error);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchSectionUsers();
  }, [user?.id, user?.organization?.id]);

  // Fetch initiative data when in edit mode
  useEffect(() => {
    const fetchInitiative = async () => {
      if (!initiativeId) return;

      try {
        setInitialLoading(true);
        const initiative = await initiativesApi.get(initiativeId);

        // Populate form with initiative data
        const init = initiative as any;
        setFormData({
          title: initiative.title || '',
          description: initiative.description || '',
          initiativeType: init.initiative_type || 'initiative',
          strategicGoal: initiative.strategic_goal?.toString() || '',
          weight: initiative.weight?.toString() || '',
          responsiblePerson: initiative.responsible_person || '',
          organizationalUnits: (initiative.responsible_units || []).map((id: number) => id.toString()),
          resources: initiative.resources_description || '',
          startDate: initiative.start_date || '',
          endDate: initiative.end_date || '',
          status: initiative.status || 'planning',
          partners: init.partners || '',
          partnerIds: Array.isArray(init.partner_links)
            ? init.partner_links.map((id: number) => id.toString())
            : [],
          requiredAction: init.required_action || '',
          requiredAttachmentsCount: (init.required_attachments_count ?? 0).toString(),
          employeesCount: init.employees_count?.toString() || '',
          executionFrequency: init.execution_frequency || '',
          flowchartData: init.flowchart_data ? JSON.stringify(init.flowchart_data, null, 2) : '',
          assignedEmployeeIds: Array.isArray(init.assigned_to_employee_ids)
            ? init.assigned_to_employee_ids.map((id: number) => id.toString())
            : [],
          kpiContributions: Array.isArray(init.kpi_contributions)
            ? init.kpi_contributions.map((row: any) => ({
                kpiId: String(row.kpi),
                weight: row.weight != null ? String(row.weight) : '',
              }))
            : [],
        });

        // Fetch KPIs for this initiative
        try {
          const kpisResponse = await kpisApi.list({ initiative: initiativeId, page_size: 100 });
          const existingKpis: KPIFormData[] = (kpisResponse.results || []).map((kpi: any) => ({
            id: kpi.id.toString(),
            name: kpi.name || '',
            weight: kpi.weight?.toString() || '0',
            description: kpi.description || '',
            kpiType: kpi.kpi_type || '',
            polarity: kpi.polarity || '',
            startDate: kpi.measurement_start_date || '',
            unit: kpi.unit || '',
            frequency: kpi.frequency || '',
            formula: kpi.measurement_formula || '',
            targetValue: kpi.target_value?.toString() || '0',
            dataSource: kpi.data_source || '',
            isExpanded: false,
          }));
          setKpis(existingKpis);
        } catch (error) {
          console.error('Error fetching KPIs:', error);
        }
      } catch (error) {
        console.error('Error fetching initiative:', error);
        showError(t('messages.error.loadFailed'));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitiative();
  }, [initiativeId]);

  // Scroll to KPI section function
  const scrollToKpiSection = useCallback(() => {
    if (scrollToKpi && kpiSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY.current - 100, animated: true });
      }, 100);
    }
  }, [scrollToKpi]);

  // Scroll to KPI section when data is loaded
  useEffect(() => {
    if (!initialLoading && scrollToKpi) {
      scrollToKpiSection();
    }
  }, [initialLoading, scrollToKpi, scrollToKpiSection]);

  // When `cascadedGoalIds` is null we're either still loading the cascade or
  // the user has unrestricted strategic visibility (Strategy Team / DG /
  // Director / superuser) — show every goal. Otherwise restrict the dropdown
  // to goals that have at least one strategic KPI projected onto the user's
  // own section, per DTS UC-02 → UC-03.
  const goalOptions: DropdownOption[] = (cascadedGoalIds === null
    ? goals
    : goals.filter(g => cascadedGoalIds.has(g.id))
  ).map(goal => ({
    value: goal.id.toString(),
    label: translate(goal.title),
  }));

  // Per DTS: collaborating units are SECTIONS only (level 2 in SSO).
  // Directorates and the root department aren't valid execution-level
  // collaborators, so we filter them out of the picker.
  const orgUnitOptions: DropdownOption[] = organizationalUnits
    .filter(unit => (unit as any).unit_type === 'section')
    .map(unit => ({
      value: unit.id.toString(),
      label: isArabic && (unit as any).name_ar ? (unit as any).name_ar : unit.name,
    }));

  // Curated partner catalog → MultiSelectDropdown options.
  // Display name in current locale (English fallback when an Arabic name
  // hasn't been provided), with the partner-type label appended for
  // disambiguation when several partners share a name.
  const partnerOptions: DropdownOption[] = partners.map((p) => ({
    value: p.id.toString(),
    label: isArabic
      ? `${p.name}${p.partner_type_display ? ` — ${p.partner_type_display}` : ''}`
      : (p.name_en || p.name),
  }));

  // Create empty KPI form
  const createEmptyKPI = (): KPIFormData => ({
    id: Date.now().toString(),
    name: '',
    weight: '30',
    description: '',
    kpiType: '',
    polarity: '',
    startDate: '',
    unit: '',
    frequency: '',
    formula: '',
    targetValue: '0.0',
    dataSource: '',
    isExpanded: true,
  });

  // Add new KPI
  const handleAddKPI = () => {
    setKpis([...kpis, createEmptyKPI()]);
  };

  // Remove KPI — open confirmation first
  const handleRemoveKPI = (kpiId: string) => {
    const target = kpis.find(k => k.id === kpiId);
    const numericId = parseInt(kpiId, 10);
    const backendId = !isNaN(numericId) && numericId < 1000000 ? numericId : null;
    setKpiPendingDelete({
      id: kpiId,
      backendId,
      name: target?.name || '',
    });
  };

  const confirmRemoveKPI = () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== id));
    setKpiPendingDelete(null);
  };

  // Toggle KPI expand/collapse
  const handleToggleKPI = (kpiId: string) => {
    setKpis(kpis.map(kpi =>
      kpi.id === kpiId ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi
    ));
  };

  // Update KPI field
  const updateKPIField = (kpiId: string, field: string, value: string) => {
    setKpis(kpis.map(kpi =>
      kpi.id === kpiId ? { ...kpi, [field]: value } : kpi
    ));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = t('validation.required', { field: t('initiatives.initiativeName') });
    }
    if (!formData.description.trim()) {
      newErrors.description = t('validation.required', { field: t('initiatives.initiativeDescription') });
    }
    if (!formData.strategicGoal) {
      newErrors.strategicGoal = t('validation.strategicGoalRequired');
    }
    // تاريخ البدء is required for an عنصر تشغيلي (per user direction).
    if (!formData.startDate) {
      newErrors.startDate = t('validation.required', { field: t('initiatives.fromDate') });
    }
    // Collaborating sections are optional per DTS — primary responsible
    // department is implicitly the proposing section head's own section.

    // DTS UC-03 step 4 — every selected KPI in the contributions list must
    // carry a valid weight in (0, 100]. Empty rows hint that the user
    // forgot a value; we surface that explicitly instead of dropping it
    // silently on save (same lesson as the goal-KPI silent-drop bug).
    for (const c of formData.kpiContributions) {
      const w = parseFloat(c.weight);
      if (!c.weight || isNaN(w) || w <= 0 || w > 100) {
        newErrors.kpiContributions = 'لكل مؤشر مختار حدد وزن المبادرة داخله (أكبر من 0 وحتى 100).';
        break;
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // Validate KPI required fields
    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) {
      showError(kpiError);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequired'));
      return;
    }

    setLoading(true);
    try {
      // Parse flowchart data — accepts plain text or JSON; stored as JSON.
      let flowchartParsed: any = null;
      if (formData.flowchartData?.trim()) {
        try {
          flowchartParsed = JSON.parse(formData.flowchartData);
        } catch {
          // If user entered free text, wrap it as a simple description
          flowchartParsed = { description: formData.flowchartData };
        }
      }

      // Prepare initiative data
      const initiativeData: Record<string, any> = {
        title: formData.title,
        description: formData.description,
        initiative_type: formData.initiativeType,
        strategic_goal: parseInt(formData.strategicGoal),
        responsible_units: formData.organizationalUnits.map((id: string) => parseInt(id)),
        resources_description: formData.resources,
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
        status: formData.status,
        // Type-specific fields (sent for all; backend ignores irrelevant ones)
        partners: formData.partners || '',
        partner_links: formData.partnerIds.map((id: string) => parseInt(id, 10)),
        required_action: formData.requiredAction || '',
        required_attachments_count: parseInt(formData.requiredAttachmentsCount) || 0,
        employees_count: showEmployeesCount && formData.employeesCount
          ? parseInt(formData.employeesCount)
          : null,
        execution_frequency: showFrequency ? formData.executionFrequency : '',
        flowchart_data: showFlowchart ? flowchartParsed : null,
        assigned_to_employee_ids: formData.assignedEmployeeIds.map((id: string) => parseInt(id, 10)),
        // DTS UC-03 step 4 — per-KPI contribution weights for the M:N
        // between this initiative and the strategic KPIs it serves.
        kpi_contributions: formData.kpiContributions
          .filter((c) => c.kpiId && c.weight !== '')
          .map((c) => ({
            kpi: parseInt(c.kpiId, 10),
            weight: parseFloat(c.weight),
          })),
      };

      let savedInitiative;

      // After a create, createdInitiativeId pins this element so a retry
      // (e.g. fixing a KPI that failed) updates it instead of creating a
      // duplicate.
      const effectiveId = initiativeId ?? createdInitiativeId;
      if (effectiveId) {
        // Update existing initiative
        savedInitiative = await initiativesApi.update(effectiveId, initiativeData);
      } else {
        // Create new initiative
        savedInitiative = await initiativesApi.create(initiativeData);
        setCreatedInitiativeId(savedInitiative.id);
      }

      // Delete removed KPIs from backend
      let kpiDeleteFailed = 0;
      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
          } catch (deleteError: any) {
            kpiDeleteFailed++;
          }
        }
      }
      if (kpiDeleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Handle KPIs - create new ones and update existing ones
      const validKpis = kpis.filter(kpi => kpi.name.trim());
      const kpiErrors: string[] = [];
      if (validKpis.length > 0) {
        const savedId = savedInitiative?.id || initiativeId;
        for (const kpi of validKpis) {
          const kpiData = {
            name: kpi.name,
            description: kpi.description,
            initiative: savedId,
            kpi_type: (kpi.kpiType || 'operational') as KPIType,
            polarity: (kpi.polarity || 'positive') as Polarity,
            unit: kpi.unit || '%',
            frequency: (kpi.frequency || 'monthly') as Frequency,
            target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
            weight: kpi.weight || '0',
            measurement_start_date: kpi.startDate || null,
            measurement_formula: kpi.formula || '',
            data_source: kpi.dataSource || '',
            source_type: 'initiative',
          };

          // Check if this is an existing KPI (small numeric ID) or new one (timestamp ID)
          const numericId = parseInt(kpi.id, 10);
          if (!isNaN(numericId) && numericId < 1000000) {
            try {
              await kpisApi.update(numericId, kpiData);
            } catch (updateError: any) {
              const errMsg = extractKpiErrorMessage(updateError?.response?.data);
              kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI ${kpi.id} update failed:`, updateError?.response?.data);
            }
          } else {
            try {
              const created = await kpisApi.create(kpiData);
              // Pin the backend id onto the local row so that if OTHER KPIs in
              // this save failed and the user fixes + re-saves, this one is
              // updated rather than created again (no duplicates).
              if (created?.id) {
                const newId = String(created.id);
                setKpis(prev => prev.map(k => (k.id === kpi.id ? { ...k, id: newId } : k)));
              }
            } catch (createError: any) {
              const errMsg = extractKpiErrorMessage(createError?.response?.data);
              kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI create failed:`, createError?.response?.data);
            }
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.all });

      // Some indicators failed to save. The element itself WAS saved, so don't
      // pretend everything succeeded — surface a blocking dialog naming each
      // failed KPI and its reason, and keep the user here so they can fix and
      // re-save (which now updates the same element via createdInitiativeId).
      if (kpiErrors.length > 0) {
        setKpiSaveError({
          message: t('kpis.savePartialFailed', { count: kpiErrors.length }) + '\n\n' + kpiErrors.join('\n'),
          savedId: savedInitiative.id,
        });
        return;
      }

      showSuccess(isEditMode ? t('initiatives.updateSuccess') : t('initiatives.createSuccess'));
      if (isEditMode) {
        navigation.goBack();
      } else {
        navigation.navigate('InitiativeDetail', { id: savedInitiative.id });
      }
    } catch (error: any) {
      console.error('Error saving initiative:', error);
      console.error('Error response:', error.response?.data);

      // Build detailed error message
      let errorMessage = isEditMode ? t('messages.error.saveFailed') : t('messages.error.saveFailed');
      const errorData = error.response?.data;

      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.non_field_errors?.[0]) {
          errorMessage = errorData.non_field_errors[0];
        } else {
          // Check for field-specific errors
          const fieldErrors: string[] = [];
          const fieldNames: Record<string, string> = {
            title: t('initiatives.initiativeName'),
            description: t('common.description'),
            strategic_goal: t('initiatives.linkedGoal'),
            responsible_person: t('initiatives.responsible'),
            start_date: t('initiatives.startDate'),
            end_date: t('initiatives.endDate'),
            budget: t('initiatives.budget'),
            status: t('initiatives.status'),
          };

          for (const [field, messages] of Object.entries(errorData)) {
            if (Array.isArray(messages) && messages.length > 0) {
              const fieldName = fieldNames[field] || field;
              fieldErrors.push(`${fieldName}: ${messages[0]}`);
            }
          }

          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(' | ');
          }
        }
      }

      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Render KPI Form Card
  const renderKPICard = (kpi: KPIFormData, index: number) => (
    <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
      {/* KPI Header */}
      <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.inputBorder }]}>
        <View style={styles.kpiCardHeaderLeft}>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => handleToggleKPI(kpi.id)}
          >
            {kpi.isExpanded ? (
              <ChevronUpIcon size={20} color={themeColors.iconColor} />
            ) : (
              <ChevronDownIcon size={20} color={themeColors.iconColor} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => handleRemoveKPI(kpi.id)}
          >
            <TrashIcon size={18} color={themeColors.iconColor} />
          </TouchableOpacity>
        </View>
        <View style={styles.kpiCardHeaderRight}>
          <Text style={[styles.kpiCardTitle, { color: themeColors.inputText }]}>
            {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
          </Text>
          <View style={[styles.kpiNumberBadge, { backgroundColor: themeColors.kpiNumber }]}>
            <Text style={[styles.kpiNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
          </View>
          <DragHandleIcon size={20} color={themeColors.iconColor} />
        </View>
      </View>

      {/* KPI Content (Collapsible) */}
      {kpi.isExpanded && (
        <View style={styles.kpiCardContent}>
          {/* Basic Information */}
          <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('goals.basicInfo')}</Text>

            <View style={[styles.row, isMobile && styles.rowMobile]}>
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('goals.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={kpi.weight}
                    onChangeText={(v) => updateKPIField(kpi.id, 'weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>

              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.name}
                  onChangeText={(v) => updateKPIField(kpi.id, 'name', v)}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={kpi.description}
                onChangeText={(v) => updateKPIField(kpi.id, 'description', v)}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          {/* Classification */}
          <View style={[styles.subsection, { zIndex: 30, backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.classification')}</Text>

            <View style={[styles.row, { zIndex: 20 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={kpi.polarity}
                  options={getPolarityOptions(t)}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => updateKPIField(kpi.id, 'polarity', v)}
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={kpi.kpiType}
                  options={getKpiTypeOptions(t)}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => updateKPIField(kpi.id, 'kpiType', v)}
                />
              </View>
            </View>

          </View>

          {/* Measurement */}
          <View style={[styles.subsection, { zIndex: 20, backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.measurement')}</Text>

            <View style={styles.row}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementStartDate')}</Text>
                </View>
                <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  {Platform.OS === 'web' ? (
                    <input
                      type="date"
                      value={kpi.startDate}
                      onChange={(e: any) => updateKPIField(kpi.id, 'startDate', e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        color: isDark ? '#E5E7EB' : '#414042',
                        textAlign: 'right' as const,
                        direction: 'rtl' as const,
                        padding: '10px 0',
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      <CalendarIcon size={16} color={themeColors.iconColor} />
                      <TextInput
                        style={[styles.dateInput, { color: themeColors.inputText }]}
                        value={kpi.startDate}
                        onChangeText={(v) => updateKPIField(kpi.id, 'startDate', v)}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor={themeColors.placeholder}
                        textAlign="right"
                      />
                    </>
                  )}
                </View>
              </View>
            </View>

            <View style={[styles.row, { zIndex: 10 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementFrequency')}</Text>
                </View>
                <Dropdown
                  value={kpi.frequency}
                  options={getFrequencyOptions(t)}
                  placeholder={t('kpis.selectFrequency')}
                  onChange={(v) => updateKPIField(kpi.id, 'frequency', v)}
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementUnit')}</Text>
                </View>
                <Dropdown
                  value={kpi.unit}
                  options={getUnitOptions(t)}
                  placeholder={t('kpis.selectUnitHint')}
                  onChange={(v) => updateKPIField(kpi.id, 'unit', v)}
                />
              </View>
            </View>

            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementFormula')}</Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={kpi.formula}
                onChangeText={(v) => updateKPIField(kpi.id, 'formula', v)}
                placeholder={t('kpis.formulaPlaceholder')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            <View style={styles.row}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.dataSource')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.dataSource}
                  onChangeText={(v) => updateKPIField(kpi.id, 'dataSource', v)}
                  placeholder={t('kpis.dataSourcePlaceholder')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.targetValue')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.targetValue}
                  onChangeText={(v) => updateKPIField(kpi.id, 'targetValue', normalizeArabicNumbers(v))}
                  keyboardType="numeric"
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>
          </View>
        </View>
      )}
    </View>
  );

  // Show loading while fetching initiative data in edit mode
  if (initialLoading) {
    return (
      <PageContainer>
        <View style={styles.loadingFullContainer}>
          <ActivityIndicator size="large" color="#A9211F" />
          <Text style={[styles.loadingText, { color: themeColors.iconColor }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('navigation.initiatives'), route: 'Initiatives' },
            { label: isEditMode ? t('initiatives.editInitiative') : t('initiatives.addInitiative') },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, isMobile && { padding: 16 }, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('initiatives.editInitiative') : t('initiatives.addInitiative')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { zIndex: 30, backgroundColor: themeColors.section, borderColor: themeColors.inputBorder }, isMobile && { padding: 16 }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('goals.basicInfo')}</Text>

            {/* Row 1: Type selector (left) + Initiative Title (right) */}
            <View style={[styles.row, { zIndex: 25 }, isMobile && { flexDirection: 'column', gap: 16 }]}>
              {/* Type selector - Left side */}
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>نوع العنصر التشغيلي</Text>
                </View>
                <Dropdown
                  value={formData.initiativeType}
                  options={INITIATIVE_TYPES.map(opt => ({ value: opt.value, label: opt.label }))}
                  placeholder="اختر النوع"
                  onChange={(v) => updateField('initiativeType', v as typeof formData.initiativeType)}
                />
              </View>

              {/* Initiative Title - Right side */}
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.initiativeTitle')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.title && styles.inputError]}
                  value={formData.title}
                  onChangeText={(v) => updateField('title', v)}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
                {errors.title && <Text style={styles.errorText}>{errors.title}</Text>}
              </View>
            </View>

            {/* Row 2: Description - Full width */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.initiativeDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.description && styles.inputError]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
              {errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
            </View>

            {/* Row 3: Strategic Goal (right) + Organizational Unit (left) */}
            <View style={[styles.row, { zIndex: 20 }, isMobile && { flexDirection: 'column', gap: 16 }]}>
              {/* Organizational Unit - Left side (optional per DTS) */}
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.implementationResponsibility')}</Text>
                </View>
                {unitsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <ActivityIndicator size="small" color="#A9211F" />
                  </View>
                ) : orgUnitOptions.length === 0 ? (
                  <View style={styles.noDataContainer}>
                    <Text style={styles.noDataText}>{t('organizations.noUnits')}</Text>
                  </View>
                ) : (
                  <MultiSelectDropdown
                    values={formData.organizationalUnits}
                    options={orgUnitOptions}
                    placeholder={t('initiatives.selectResponsibleUnits')}
                    onChange={(vals) => {
                      setFormData(prev => ({ ...prev, organizationalUnits: vals }));
                      if (errors.organizationalUnits) {
                        setErrors(prev => ({ ...prev, organizationalUnits: '' }));
                      }
                    }}
                  />
                )}
                {errors.organizationalUnits && <Text style={styles.errorText}>{errors.organizationalUnits}</Text>}
              </View>

              {/* Strategic Goal - Right side */}
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.strategicGoal')}</Text>
                </View>
                {goalsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <ActivityIndicator size="small" color="#A9211F" />
                  </View>
                ) : goals.length === 0 ? (
                  <View style={styles.noDataContainer}>
                    <Text style={styles.noDataText}>{t('dashboard.noGoalsCurrently')}</Text>
                  </View>
                ) : (
                  <Dropdown
                    value={formData.strategicGoal}
                    options={goalOptions}
                    placeholder={t('risks.selectStrategicGoal')}
                    onChange={(v) => updateField('strategicGoal', v)}
                  />
                )}
                {errors.strategicGoal && <Text style={styles.errorText}>{errors.strategicGoal}</Text>}
              </View>
            </View>

            {/* DTS UC-03 step 4 — KPI contributions: pick the strategic
                KPIs this initiative serves and set the weight of THIS
                initiative inside each one. The aggregate-source engine
                (DTS 4.3 level 4b) consumes these weights. */}
            <View style={[styles.field, { zIndex: 19 }]}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>المؤشرات الاستراتيجية ووزن المبادرة داخل كل مؤشر</Text>
              </View>
              <View style={{
                flexDirection: 'row-reverse',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 10,
                paddingVertical: 8,
                borderRadius: 6,
                marginBottom: 8,
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : '#EFF6FF',
              }}>
                <Text style={{
                  flex: 1,
                  fontSize: 12,
                  fontFamily: FONTS.regular,
                  textAlign: 'right',
                  lineHeight: 18,
                  color: isDark ? '#93C5FD' : '#1E40AF',
                }}>
                  ℹ️ اختر المؤشرات الاستراتيجية المرتبطة بهذه المبادرة، وحدد وزن المبادرة داخل كل مؤشر (مثال: 40% في مؤشر الرضا، 20% في مؤشر الامتثال). مجموع أوزان كل المبادرات داخل المؤشر الواحد ≤ 100%.
                </Text>
              </View>
              {cascadedKpis.length === 0 ? (
                <View style={styles.noDataContainer}>
                  <Text style={styles.noDataText}>لا توجد مؤشرات استراتيجية مُسقطة على قسمك</Text>
                </View>
              ) : (
                <>
                  <MultiSelectDropdown
                    values={formData.kpiContributions.map((c) => c.kpiId)}
                    options={cascadedKpis.map((k) => ({
                      value: String(k.id),
                      label: k.strategic_goal_title
                        ? `${k.name} — ${k.strategic_goal_title}`
                        : k.name,
                    }))}
                    placeholder="اختر مؤشراً واحداً أو أكثر"
                    onChange={(vals) => {
                      setFormData((prev) => {
                        const existing = new Map(
                          prev.kpiContributions.map((c) => [c.kpiId, c.weight])
                        );
                        const next = vals.map((v) => ({
                          kpiId: v,
                          weight: existing.get(v) ?? '',
                        }));
                        return { ...prev, kpiContributions: next };
                      });
                    }}
                  />

                  {errors.kpiContributions && (
                    <Text style={[styles.errorText, { marginTop: 6 }]}>{errors.kpiContributions}</Text>
                  )}

                  {formData.kpiContributions.length > 0 && (
                    <View style={{ marginTop: 12, gap: 8 }}>
                      {formData.kpiContributions.map((row, idx) => {
                        const kpi = cascadedKpis.find((k) => String(k.id) === row.kpiId);
                        return (
                          <View
                            key={row.kpiId}
                            style={{
                              flexDirection: 'row-reverse',
                              alignItems: 'center',
                              gap: 12,
                              paddingHorizontal: 12,
                              paddingVertical: 10,
                              borderRadius: 8,
                              borderWidth: 1,
                              borderColor: themeColors.inputBorder,
                              backgroundColor: themeColors.input,
                            }}
                          >
                            <View style={{ flex: 1 }}>
                              <Text
                                style={{
                                  fontSize: 13,
                                  fontFamily: FONTS.medium,
                                  color: themeColors.inputText,
                                  textAlign: 'right',
                                }}
                                numberOfLines={1}
                              >
                                {kpi?.name ?? `مؤشر #${row.kpiId}`}
                              </Text>
                              {kpi?.strategic_goal_title && (
                                <Text
                                  style={{
                                    fontSize: 11,
                                    fontFamily: FONTS.regular,
                                    color: themeColors.label,
                                    textAlign: 'right',
                                    marginTop: 2,
                                  }}
                                  numberOfLines={1}
                                >
                                  {kpi.strategic_goal_title}
                                </Text>
                              )}
                            </View>
                            <View style={{ flexDirection: 'row-reverse', alignItems: 'center', gap: 4 }}>
                              <TextInput
                                value={row.weight}
                                onChangeText={(txt) => {
                                  const cleaned = normalizeArabicNumbers(txt).replace(/[^0-9.]/g, '');
                                  setFormData((prev) => ({
                                    ...prev,
                                    kpiContributions: prev.kpiContributions.map((c, i) =>
                                      i === idx ? { ...c, weight: cleaned } : c
                                    ),
                                  }));
                                }}
                                keyboardType="numeric"
                                placeholder="0"
                                placeholderTextColor={themeColors.label}
                                style={{
                                  width: 70,
                                  paddingHorizontal: 8,
                                  paddingVertical: 6,
                                  borderRadius: 6,
                                  borderWidth: 1,
                                  borderColor: themeColors.inputBorder,
                                  backgroundColor: themeColors.background,
                                  color: themeColors.inputText,
                                  textAlign: 'center',
                                  fontSize: 13,
                                  fontFamily: FONTS.regular,
                                }}
                              />
                              <Text style={{ color: themeColors.label, fontSize: 13 }}>%</Text>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  )}
                </>
              )}
            </View>

            {/* Row 3.5: Reassignment to section team members (DTS UC-03 step 9, FR-22) */}
            <View style={[styles.field, { zIndex: 18 }]}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>تكليف موظف بالتنفيذ (اختياري)</Text>
              </View>
              <View style={{
                flexDirection: 'row-reverse',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 10,
                paddingVertical: 8,
                borderRadius: 6,
                marginBottom: 8,
                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : '#EFF6FF',
              }}>
                <Text style={{
                  flex: 1,
                  fontSize: 12,
                  fontFamily: FONTS.regular,
                  textAlign: 'right',
                  lineHeight: 18,
                  color: isDark ? '#93C5FD' : '#1E40AF',
                }}>
                  ℹ️ إذا لم تختر أي موظف، سيتم التنفيذ من خلالك مباشرة
                </Text>
              </View>
              {employeesLoading ? (
                <View style={[styles.loadingContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  <ActivityIndicator size="small" color="#A9211F" />
                </View>
              ) : sectionEmployees.length === 0 ? (
                <View style={styles.noDataContainer}>
                  <Text style={styles.noDataText}>لا يوجد موظفون في قسمك</Text>
                </View>
              ) : (
                <MultiSelectDropdown
                  values={formData.assignedEmployeeIds}
                  options={sectionEmployees.map((u) => ({
                    value: u.id.toString(),
                    label: u.full_name
                      || `${u.first_name || ''} ${u.last_name || ''}`.trim()
                      || u.username
                      || u.email,
                  }))}
                  placeholder="اختر موظف أو أكثر للتنفيذ"
                  onChange={(vals) => {
                    setFormData((prev) => ({ ...prev, assignedEmployeeIds: vals }));
                  }}
                />
              )}
            </View>

            {/* Row 4: Resources - Full width */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.resources')}</Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.resources}
                onChangeText={(v) => updateField('resources', v)}
                placeholder={t('initiatives.resourcesPlaceholder')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Row 4.5: Partners - Full width (DTS UC-03 step 7 + FR-27) */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>الشركاء (للعلم فقط)</Text>
              </View>
              {partnersLoading ? (
                <View style={[styles.loadingContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                  <ActivityIndicator size="small" color="#A9211F" />
                </View>
              ) : partnerOptions.length === 0 ? (
                <View style={styles.noDataContainer}>
                  <Text style={styles.noDataText}>
                    لا يوجد شركاء مسجّلون بعد — يمكن لفريق الاستراتيجية إضافتهم من قائمة "الشركاء" في القائمة الجانبية.
                  </Text>
                </View>
              ) : (
                <MultiSelectDropdown
                  values={formData.partnerIds}
                  options={partnerOptions}
                  placeholder="اختر الجهات الشريكة"
                  onChange={(vals) => setFormData(prev => ({ ...prev, partnerIds: vals }))}
                />
              )}
              {/* Legacy free-text — show only if there's old data and no curated links yet,
                  so we don't lose the historical context until the user re-attaches partners. */}
              {formData.partners && formData.partnerIds.length === 0 ? (
                <Text style={[styles.label, { color: themeColors.label, fontSize: 12, marginTop: 8, opacity: 0.7 }]}>
                  بيانات قديمة (نص حر): {formData.partners}
                </Text>
              ) : null}
            </View>

            {/* Row 4.6: Required Action + Attachments Count (DTS UC-03) */}
            <View style={[styles.row, isMobile && { flexDirection: 'column', gap: 16 }]}>
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>عدد المرفقات المطلوبة</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.requiredAttachmentsCount}
                  onChangeText={(v) => updateField('requiredAttachmentsCount', v.replace(/\D/g, ''))}
                  placeholder="0"
                  placeholderTextColor={themeColors.placeholder}
                  keyboardType="numeric"
                  textAlign="right"
                />
              </View>
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>الإجراء المطلوب</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.requiredAction}
                  onChangeText={(v) => updateField('requiredAction', v)}
                  placeholder="ما الإجراء المطلوب لإغلاق دورة قياس؟"
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Row 4.7: Type-specific fields (process / operational_activity) */}
            {(showEmployeesCount || showFrequency) && (
              <View style={[styles.row, isMobile && { flexDirection: 'column', gap: 16 }]}>
                {showEmployeesCount && (
                  <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.label, { color: themeColors.label }]}>عدد الموظفين المطلوبين</Text>
                    </View>
                    <TextInput
                      style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                      value={formData.employeesCount}
                      onChangeText={(v) => updateField('employeesCount', v.replace(/\D/g, ''))}
                      placeholder="0"
                      placeholderTextColor={themeColors.placeholder}
                      keyboardType="numeric"
                      textAlign="right"
                    />
                  </View>
                )}
                {showFrequency && (
                  <View style={[styles.fieldHalf, { zIndex: 15 }, isMobile && { width: '100%' }]}>
                    <View style={styles.labelRow}>
                      {formData.initiativeType === 'operational_activity' && <Text style={styles.requiredStar}>*</Text>}
                      <Text style={[styles.label, { color: themeColors.label }]}>دورية التنفيذ</Text>
                    </View>
                    <Dropdown
                      value={formData.executionFrequency}
                      options={FREQUENCY_OPTIONS}
                      placeholder="اختر الدورية"
                      onChange={(v) => updateField('executionFrequency', v)}
                    />
                  </View>
                )}
              </View>
            )}

            {/* Row 4.8: Flowchart (process only) */}
            {showFlowchart && (
              <View style={styles.field}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>مخطط التدفق (Flowchart)</Text>
                </View>
                <Text style={{ fontSize: 12, color: themeColors.placeholder, textAlign: 'right', marginBottom: 6 }}>
                  أدخل وصف خطوات التدفق (سيتم تطوير محرر تفاعلي لاحقاً). يقبل JSON أو نص حر.
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText, minHeight: 100 }]}
                  value={formData.flowchartData}
                  onChangeText={(v) => updateField('flowchartData', v)}
                  placeholder="مثال: 1) استلام التعديلات 2) المراجعة 3) الاحتساب 4) الإيداع"
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                  multiline
                  numberOfLines={4}
                />
              </View>
            )}

            {/* Row 5: Planned Period */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.plannedPeriod')}</Text>
              </View>
              <View style={[styles.row, { marginBottom: 0, gap: 24 }, isMobile && { flexDirection: 'column', gap: 16 }]}>
                {/* End Date - Left side */}
                <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                  <View style={styles.labelRow}>
                    {isEndDateRequired && <Text style={styles.requiredStar}>*</Text>}
                    <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.toDate')}</Text>
                  </View>
                  <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e: any) => updateField('endDate', e.target.value)}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          fontSize: 14,
                          fontFamily: 'inherit',
                          color: isDark ? '#E5E7EB' : '#414042',
                          textAlign: 'right' as const,
                          direction: 'rtl' as const,
                          padding: '7.5px 0',
                          cursor: 'pointer',
                        }}
                      />
                    ) : (
                      <>
                        <CalendarIcon size={16} color={themeColors.iconColor} />
                        <TextInput
                          style={[styles.dateInput, { color: themeColors.inputText }]}
                          value={formData.endDate}
                          onChangeText={(v) => updateField('endDate', v)}
                          placeholder={t('initiatives.selectEndDate')}
                          placeholderTextColor={themeColors.placeholder}
                          textAlign="right"
                        />
                      </>
                    )}
                  </View>
                </View>

                {/* Start Date - Right side */}
                <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                  <View style={styles.labelRow}>
                    <Text style={styles.requiredStar}>*</Text>
                    <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.fromDate')}</Text>
                  </View>
                  <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e: any) => updateField('startDate', e.target.value)}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          fontSize: 14,
                          fontFamily: 'inherit',
                          color: isDark ? '#E5E7EB' : '#414042',
                          textAlign: 'right' as const,
                          direction: 'rtl' as const,
                          padding: '7.5px 0',
                          cursor: 'pointer',
                        }}
                      />
                    ) : (
                      <>
                        <CalendarIcon size={16} color={themeColors.iconColor} />
                        <TextInput
                          style={[styles.dateInput, { color: themeColors.inputText }]}
                          value={formData.startDate}
                          onChangeText={(v) => updateField('startDate', v)}
                          placeholder={t('initiatives.selectStartDate')}
                          placeholderTextColor={themeColors.placeholder}
                          textAlign="right"
                        />
                      </>
                    )}
                  </View>
                  {errors.startDate && <Text style={styles.errorText}>{errors.startDate}</Text>}
                </View>
              </View>
            </View>

          </View>

          {/* KPIs Section */}
          <View
            style={[styles.kpisSection, { backgroundColor: themeColors.section }]}
            onLayout={(event) => {
              kpiSectionY.current = event.nativeEvent.layout.y;
            }}
          >
            {/* Section Header */}
            <TouchableOpacity
              style={styles.kpisSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpisSectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.performanceIndicators')}</Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpisSectionContent}>
                {/* KPI Cards */}
                {kpis.map((kpi, index) => renderKPICard(kpi, index))}

                {/* Add KPI Button */}
                <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={handleAddKPI}>
                  <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('kpis.addKPI')}</Text>
                  <PlusIcon size={16} color={themeColors.inputText} />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Action Buttons */}
          <View style={[styles.actionButtons, isMobile && { flexDirection: 'column', width: '100%' }]}>
            <TouchableOpacity
              style={[styles.saveButton, { minHeight: 44 }, loading && styles.saveButtonDisabled, isMobile && { width: '100%' }]}
              onPress={handleSave}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{t('common.save')}</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.cancelButton, { minHeight: 44, backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }, isMobile && { width: '100%' }]}
              onPress={handleCancel}
              disabled={loading}
            >
              <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <ConfirmModal
        visible={kpiSaveError !== null}
        type="warning"
        title={t('kpis.savePartialTitle')}
        message={kpiSaveError?.message ?? ''}
        confirmText={t('kpis.viewElement')}
        cancelText={t('kpis.stayAndFix')}
        onConfirm={() => {
          const id = kpiSaveError?.savedId;
          setKpiSaveError(null);
          if (id) navigation.navigate('InitiativeDetail', { id });
        }}
        onCancel={() => setKpiSaveError(null)}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 24,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Main Card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },
  // Section
  section: {
    backgroundColor: '#F8FAFC',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 24,
    marginBottom: 16,
    overflow: 'visible',
    gap: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: '#414042',
    textAlign: 'right',
  },
  // Field styles
  field: {
    marginBottom: 16,
  },
  fieldSmall: {
    width: '30%',
    minWidth: 100,
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  rowMobile: {
    flexDirection: 'column',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  requiredStar: {
    fontSize: 14,
    color: '#D62728',
  },
  // Input styles
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  // Percent Input
  percentInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  percentInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  percentLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  weightInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  percentSymbol: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  setWeightButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  setWeightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dateInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Loading
  loadingContainer: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  noDataContainer: {
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#F59E0B',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  noDataText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#92400E',
    textAlign: 'center',
  },
  loadingFullContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownDisabled: {
    backgroundColor: '#F1F5F9',
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownPlaceholder: {
    color: '#64748B',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    marginTop: 4,
    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
    elevation: 20,
    zIndex: 9999,
    maxHeight: 200,
    paddingVertical: 8,
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // KPIs Section
  kpisSection: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    overflow: 'visible',
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 8,
    minHeight: 44,
  },
  kpisSectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
  },
  kpisSectionContent: {
    padding: 16,
    paddingTop: 0,
    overflow: 'visible',
  },
  // KPI Card
  kpiCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 12,
    overflow: 'visible',
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  kpiHeaderButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  kpiNumberBadge: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiNumberText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  kpiCardContent: {
    padding: 16,
    overflow: 'visible',
  },
  subsection: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  subsectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
    marginBottom: 16,
  },
  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    borderStyle: 'dashed',
    backgroundColor: '#FFFFFF',
    minHeight: 44,
  },
  addKpiButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minWidth: 80,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#D1D5DB',
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Error styles
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },
});
